import React, { useEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import "../styles/checkout-page.css";
import {
  getTemplateData,
  getCouponItems as fetchCouponItems,
  isPackInStock,
} from "../services/apiservice";
import Footer from "./Footer";
import PackModal from "./PackModal";
import "../styles/Multi-pack-list.css";
import GradientHeader from "./GradientHeader";

const DEFAULT_PACK_IMAGE =
  "https://content.swageazy.com/website/default_pack_fallback_img.webp";

const getProductImage = (product) => {
  if (product?.mockup_images?.length > 0) {
    const priority1Mockup = product.mockup_images.find(
      (img) => img.priority === 1,
    );
    return (
      (priority1Mockup || product.mockup_images[0])?.image_url ||
      DEFAULT_PACK_IMAGE
    );
  }
  if (product?.images?.length > 0) {
    return product.images[0].image_url || DEFAULT_PACK_IMAGE;
  }
  return DEFAULT_PACK_IMAGE;
};

const accentPerfectPack = (text) => {
  if (typeof text !== "string" || text.length === 0) return text;
  const match = text.match(/^(.*?)(Perfect Packs?)$/i);
  if (!match) return text;
  return (
    <>
      {match[1]}
      <span className="pack-heading-accent">{match[2]}</span>
    </>
  );
};

/** Same mosaic as dashboard: 1–3 tiles as-is, 4 tiles, 5+ is 3 images + "+N More". */
const ProductCollage = ({ products }) => {
  const list = Array.isArray(products) ? products : [];
  const total = list.length;
  const tiles = total === 0 ? [null] : list.slice(0, 4);
  const mosaicCount = Math.min(tiles.length, 4);
  const showMoreOnLast = total > 4;

  return (
    <div className="pack-mosaic" data-count={mosaicCount}>
      {tiles.map((product, index) => (
        <div className="mosaic-cell" key={index}>
          {showMoreOnLast && index === 3 ? (
            <span className="mosaic-more">+{total - 3} More</span>
          ) : (
            <img
              src={product ? getProductImage(product) : DEFAULT_PACK_IMAGE}
              alt={product?.product_name || `Product ${index + 1}`}
              className="mosaic-image"
              onError={(event) => {
                event.target.src = DEFAULT_PACK_IMAGE;
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const CheckoutPage = () => {
  const [checkoutDetails, setCheckoutDetails] = useState({
    backroundColor: "",
    logoImageUrl: "",
    buttonColor: "",
    has_multiple_choice: "",
    footerColor: "",
    footerText: "",
    customerId: null,
    headerColor: "",
    pageContent1: "",
    pageContent2: "",
    singlePackHeading: "",
    multiplePackHeading: "",
    multiplePackChoiceHeading: "",
    multiplePackContent: "",
    multiplePackChoiceContent: "",
  });
  const queryParameters = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [showProducts, setShowProducts] = useState(1);
  const [open, setOpen] = useState(false);
  const [swagpacks, setSwagpacks] = useState([]);
  const [showPackDetails, setShowPackDetails] = useState();
  const [selectedPack, setSelectedPack] = useState(null);
  const [preSelectedProduct, setPreSelectedProduct] = useState(null);
  const [selectedPackIndex, setSelectedPackIndex] = useState(null);
  const token = queryParameters.get("token");
  const navigate = useNavigate();
  const { state, pathname, search } = useLocation();
  // Set by the details form when a submit came back PACK_OUT_OF_STOCK. Kept for
  // this visit only: router state survives a refresh, and in_stock covers later loads.
  const [outOfStockPackIds] = useState(state?.outOfStockPackIds || []);
  const [outOfStockMessage] = useState(state?.outOfStockMessage || "");
  let params = { token: token };

  useEffect(() => {
    if (state?.outOfStockPackIds || state?.outOfStockMessage) {
      navigate(`${pathname}${search}`, { replace: true, state: null });
    }
  }, []);
  const [customerId, setCustomerId] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [packContainerRef, setPackContainerRef] = useState(null);

  useEffect(() => {
    if (token === null || token === "" || token === undefined) {
      setLoading(false);
      navigate("/");
      return;
    }
    Promise.allSettled([getTemplateIdFromCoupon(), getCouponItems()]).then(() =>
      setLoading(false),
    );
  }, []);

  const inStockPacks = swagpacks.filter((pack) =>
    isPackInStock(pack, outOfStockPackIds),
  );
  const allPacksOutOfStock = swagpacks.length > 0 && inStockPacks.length === 0;

  function getTemplateIdFromCoupon() {
    return getTemplateData()
      .then((e) => {
        setCheckoutDetails({
          backgroundColor: e.data.result.pageBackgroundColor,
          logoImageUrl: e.data.result.logo_image_url,
          buttonColor: e.data.result.buttonColor,
          has_multiple_choice: e.data.result.has_multiple_choice,
          footerColor: e.data.result.footer_color || "",
          footerText: e.data.result.footer_text || "",
          customerId: e.data.result.customer_id ?? null,
          headerColor: e.data.result.header_color || "",
          pageContent1: e.data.result.product_page_content_1 || "",
          pageContent2: e.data.result.product_page_content_2 || "",
          singlePackHeading:
            e.data.result.product_page_heading_single_pack || "",
          multiplePackHeading:
            e.data.result.product_page_heading_multiple_pack || "",
          multiplePackChoiceHeading:
            e.data.result.product_page_heading_multiple_pack_choice || "",
          multiplePackContent:
            e.data.result.product_page_heading_multiple_pack_content || "",
          multiplePackChoiceContent:
            e.data.result.product_page_heading_multiple_pack_choice_content ||
            "",
        });
      })
      .catch(() => {});
  }

  function getCouponItems() {
    return fetchCouponItems(token)
      .then((res) => {
        if (res.data.success && res.data.result) {
          if (
            res.data.result.swagpacks &&
            res.data.result.swagpacks.length > 0
          ) {
            // Out-of-stock packs go last; sort is stable, so API order holds otherwise.
            setSwagpacks(
              [...res.data.result.swagpacks].sort(
                (a, b) =>
                  isPackInStock(b, outOfStockPackIds) -
                  isPackInStock(a, outOfStockPackIds),
              ),
            );
            setShowProducts(res.data.result.show_products);
            setCustomerId(res.data.result.customer_id);
            // Direct navigation when showProducts is 0; out-of-stock packs are dropped,
            // and if none are left the page stays to show the out-of-stock state.
            const availablePacks = res.data.result.swagpacks.filter((pack) =>
              isPackInStock(pack, outOfStockPackIds),
            );
            if (
              res.data.result.show_products === 0 &&
              availablePacks.length > 0
            ) {
              navigate(`/redemption/details?token=${token}`, {
                replace: true,
                state: { selectedPack: { selectedPack: availablePacks } },
              });
              return;
            }
          } else {
            navigate({
              pathname: "/",
              search: `?${createSearchParams(params)}`,
            });
          }
        }
      })
      .catch((err) => {
        navigate({
          pathname: "/",
          search: `?${createSearchParams(params)}`,
        });
      });
  }

  const handlePackSelectionChange = (pack, index) => {
    if (!isPackInStock(pack, outOfStockPackIds)) return;
    setSelectedPack({
      selectedPack: [pack],
    });
    setSelectedPackIndex(index);
  };

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setShowPackDetails(null);
    setPreSelectedProduct(null);
  };

  const handleProductClick = (product, index) => {
    // When user clicks on a product, show the PackModal with the swagpack details
    // and pre-select the clicked product (works for any swagpack count)
    setShowPackDetails(swagpacks[0]);
    setPreSelectedProduct({ ...product, originalIndex: index });
    openModal();
  };

  const handleRoute = () => {
    if (allPacksOutOfStock) return;
    if (checkoutDetails.has_multiple_choice === 1) {
      if (swagpacks.length > 1 && !selectedPack) {
        console.log("No pack selected");
        return;
      }

      if (swagpacks.length === 1) {
        navigate(`/redemption/details?token=${params.token}`, {
          state: {
            selectedPack: {
              selectedPack: inStockPacks,
            },
          },
        });
      } else {
        navigate(`/redemption/details?token=${params.token}`, {
          state: { selectedPack },
        });
      }
    } else {
      setSelectedPack({ selectedPack: inStockPacks });
      navigate(`/redemption/details?token=${params.token}`, {
        state: { selectedPack: { selectedPack: inStockPacks } },
      });
    }
  };

  const showPackProducts = (pack) => {
    if (showProducts === 0) {
      setSelectedPack({
        selectedPack: [pack],
      });
      navigate(`/redemption/details?token=${params.token}`, {
        state: { selectedPack: { selectedPack: [pack] } },
      });
      return;
    }
    if (showProducts === 1) {
      setShowPackDetails(pack);
      // Auto-select first product when pack is clicked to go directly to detailed view
      if (pack?.products?.length > 0) {
        setPreSelectedProduct({ ...pack.products[0], originalIndex: 0 });
      }
      openModal();
    }
  };

  // Arrow navigation functions
  const scrollLeft = () => {
    if (packContainerRef) {
      packContainerRef.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (packContainerRef) {
      packContainerRef.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  // Check if arrows should be visible based on scroll position and content overflow
  const checkArrowsVisibility = () => {
    if (packContainerRef) {
      const { scrollLeft, scrollWidth, clientWidth } = packContainerRef;
      const hasOverflow = scrollWidth > clientWidth;

      setShowLeftArrow(hasOverflow && scrollLeft > 5);
      setShowRightArrow(
        hasOverflow && scrollLeft < scrollWidth - clientWidth - 5,
      );
    }
  };

  // Set up scroll event listener and resize observer
  useEffect(() => {
    if (packContainerRef) {
      // Initial check with a small delay to ensure DOM is fully rendered
      const initialCheck = setTimeout(() => {
        checkArrowsVisibility();
      }, 100);

      packContainerRef.addEventListener("scroll", checkArrowsVisibility);

      // Also check on window resize
      const handleResize = () => {
        checkArrowsVisibility();
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(initialCheck);
        if (packContainerRef) {
          packContainerRef.removeEventListener("scroll", checkArrowsVisibility);
        }
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [packContainerRef, swagpacks]);

  return (
    <div className="m-0 p-0">
      {loading ? (
        <div className="loading-indicator-admin"> </div>
      ) : (
        <>
          <div className="d-flex flex-column min-vh-100">
            <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0">
              <div
                // className={
                //   customerId &&
                //   Number(customerId) === Number(process.env.REACT_APP_CUSTOMER_ID)
                //     ? "top-margin-two light-header"
                //     : "top-margin-two header"
                // }
                className="top-margin-two header"
                style={{
                  background: checkoutDetails.headerColor || "#1E1665",
                }}
              >
                {checkoutDetails.logoImageUrl && (
                  <div className="d-flex">
                    <GradientHeader
                      logoSrc={checkoutDetails.logoImageUrl}
                      justifyContent="flex-start"
                      headerColor={checkoutDetails.headerColor}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="outer-div col-12 flex-grow-1">
              <div className="pack-heading-block">
                <p className="pack-heading-eyebrow">Gift for every occasion</p>
                <h1 className="pack-heading d-flex justify-content-center">
                  {swagpacks.length === 1 && showProducts === 1 ? (
                    checkoutDetails.singlePackHeading !== " " && (
                      <span>
                        {accentPerfectPack(
                          checkoutDetails.singlePackHeading ||
                            "Products in your pack",
                        )}
                      </span>
                    )
                  ) : checkoutDetails.has_multiple_choice === 1 ? (
                    checkoutDetails.multiplePackChoiceHeading !== " " && (
                      <span>
                        {accentPerfectPack(
                          checkoutDetails.multiplePackChoiceHeading ||
                            "Pick Your Perfect Pack",
                        )}
                      </span>
                    )
                  ) : checkoutDetails.multiplePackHeading !== " " ? (
                    <span>
                      {accentPerfectPack(
                        checkoutDetails.multiplePackHeading ||
                          "Pick Your Perfect Pack",
                      )}
                    </span>
                  ) : (
                    accentPerfectPack("Pick Your Perfect Pack")
                  )}
                </h1>
                <h6 className="pack-subheading d-flex justify-content-center text-center flex-column">
                  {swagpacks.length === 1 && showProducts === 1 ? (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "We're delighted to share with you the contents of your special Swag pack."}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 ||
                            "Below is the list of products included. We hope you'll find joy and satisfaction in each item."}
                        </span>
                      )}
                    </>
                  ) : checkoutDetails.has_multiple_choice === 1 ? (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "You've received a selection of gift, carefully curated for you. Please select your preferred option from the list below"}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 || ""}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {checkoutDetails.pageContent1 !== " " && (
                        <span className="mt-1">
                          {checkoutDetails.pageContent1 ||
                            "Enjoy your personally curated swag pack — thoughtfully selected and carefully crafted."}
                        </span>
                      )}
                      {checkoutDetails.pageContent2 !== " " && (
                        <span className="mt-2">
                          {checkoutDetails.pageContent2 || ""}
                        </span>
                      )}
                    </>
                  )}
                </h6>
              </div>

              <div className="row inner-div" style={{ maxWidth: "1500px" }}>
                {outOfStockMessage && (
                  <div className="checkout-stock-alert" role="alert">
                    <i
                      className="bi bi-exclamation-circle-fill"
                      aria-hidden="true"
                    />
                    <span>{outOfStockMessage}</span>
                  </div>
                )}
                <div className="pack-container-wrapper position-relative">
                  {/* Left Arrow */}
                  {showLeftArrow && (
                    <button
                      className="scroll-arrow scroll-arrow-left"
                      onClick={scrollLeft}
                      aria-label="Scroll left"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 18L9 12L15 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Right Arrow */}
                  {showRightArrow && (
                    <button
                      className="scroll-arrow scroll-arrow-right"
                      onClick={scrollRight}
                      aria-label="Scroll right"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 18L15 12L9 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}

                  <div className="row pack-container" ref={setPackContainerRef}>
                    {swagpacks.length === 1 && showProducts === 1
                      ? // Show products in the same layout as packs
                        swagpacks[0].products.map((product, index) => (
                          <div
                            className="col-xl-3 col-lg-3 col-md-3 col-sm-6 col-12 pb-3"
                            key={index}
                          >
                            <div className="col text-center">
                              <div className="rounded product-card border-none border-0 p-3 h-100">
                                <div
                                  className=""
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleProductClick(product, index)
                                  }
                                >
                                  <img
                                    src={getProductImage(product)}
                                    className="mockimg img-fluid"
                                    style={{ maxHeight: "150px" }}
                                    alt={`Product ${index}`}
                                    onError={(e) => {
                                      e.target.src = DEFAULT_PACK_IMAGE;
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="pack-name border-0 pt-2 d-flex justify-content-start">
                                <label
                                  className="card-title p-0"
                                  style={{
                                    fontWeight: "400",
                                    fontFamily: "Poppins",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    width: "100%",
                                    fontSize: "1rem",
                                  }}
                                  title={product.product_name}
                                >
                                  {product.product_name}
                                </label>
                              </div>
                            </div>
                          </div>
                        ))
                      : // Show packs with selection if has_multiple_choice is 1
                        swagpacks.map((pack, index) => {
                          const packInStock = isPackInStock(
                            pack,
                            outOfStockPackIds,
                          );
                          return (
                            <div
                              className="col-xl-3 col-lg-3 col-md-3 col-sm-6 col-12 pb-3"
                              key={index}
                            >
                              <div
                                className={`checkout-pack-card ${
                                  selectedPackIndex === index
                                    ? "is-selected"
                                    : ""
                                } ${
                                  checkoutDetails.has_multiple_choice === 1 &&
                                  packInStock
                                    ? "is-selectable"
                                    : ""
                                } ${packInStock ? "" : "is-out-of-stock"}`}
                                aria-disabled={!packInStock}
                                onClick={() =>
                                  checkoutDetails.has_multiple_choice === 1 &&
                                  handlePackSelectionChange(pack, index)
                                }
                              >
                                <div className="checkout-pack-media">
                                  <div
                                    className="checkout-pack-collage"
                                    style={{
                                      cursor:
                                        showProducts === 1
                                          ? "pointer"
                                          : "inherit",
                                    }}
                                    onClick={(e) => {
                                      if (showProducts !== 1) return;
                                      e.stopPropagation();
                                      showPackProducts(pack);
                                    }}
                                  >
                                    <ProductCollage products={pack.products} />
                                  </div>

                                  {checkoutDetails.has_multiple_choice === 1 &&
                                    packInStock && (
                                      <span
                                        className="checkout-pack-check"
                                        aria-hidden="true"
                                      >
                                        <svg
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                            fill="currentColor"
                                          />
                                        </svg>
                                      </span>
                                    )}
                                  {!packInStock && (
                                    <span className="checkout-pack-stock-badge">
                                      Out of stock
                                    </span>
                                  )}
                                  {packInStock &&
                                    Number(pack.selection_percentage) > 0 && (
                                      <span className="checkout-pack-popularity">
                                        <i
                                          className="bi bi-people-fill"
                                          aria-hidden="true"
                                        />
                                        <span className="checkout-pack-popularity-value">
                                          {Number(
                                            pack.selection_percentage || 0,
                                          )}
                                          %
                                        </span>
                                        <span className="checkout-pack-popularity-text">
                                          people chose this
                                        </span>
                                      </span>
                                    )}
                                </div>

                                <div className="checkout-pack-body">
                                  <p
                                    className="checkout-pack-title"
                                    title={pack.label}
                                  >
                                    {pack.label}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>

                <div className="row p-3 ">
                  {checkoutDetails.has_multiple_choice === 1 &&
                    swagpacks[selectedPackIndex]?.label && (
                      <p className="checkout-selected-pack">
                        You have selected '{swagpacks[selectedPackIndex].label}'
                      </p>
                    )}
                  <div
                    id="checkoutbtn"
                    className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 col-12 redeem-btn-div d-flex justify-content-center align-items-center"
                  >
                    <button
                      onClick={() => handleRoute()}
                      className="redeem-btn"
                      disabled={allPacksOutOfStock}
                      style={{
                        backgroundColor: "#6455ED",
                        border: "none",
                        color: "#fff",
                        borderRadius: "25px",
                        opacity: allPacksOutOfStock ? 0.5 : 1,
                        cursor: allPacksOutOfStock ? "not-allowed" : "pointer",
                      }}
                    >
                      Redeem now
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <Footer
              footerText={checkoutDetails.footerText}
              footerColor={checkoutDetails.footerColor}
              customerId={checkoutDetails.customerId}
            />

            <PackModal
              open={open}
              onClose={closeModal}
              packDetails={showPackDetails}
              preSelectedProduct={preSelectedProduct}
              customerId={customerId}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CheckoutPage;
